import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import {
  CalendarCheck, CalendarCog, Layers, Inbox, Settings, Trash2, Plus, ShieldAlert,
  CalendarPlus, LogOut, Lock, Download, KeyRound, Star, Newspaper, Calculator,
} from "lucide-react";
import { api, adminApi } from "../lib/api";
import { Logo } from "@/components/Logo";
import { AdminBlog } from "@/components/AdminBlog";
import { AdminQuotes } from "@/components/AdminQuotes";

const TABS = [
  { id: "bookings", label: "Prenotazioni", Icon: CalendarCheck },
  { id: "availability", label: "Disponibilità", Icon: CalendarCog },
  { id: "types", label: "Tipologie", Icon: Layers },
  { id: "contacts", label: "Richieste", Icon: Inbox },
  { id: "reviews", label: "Recensioni", Icon: Star },
  { id: "blog", label: "Blog", Icon: Newspaper },
  { id: "quotes", label: "Preventivi", Icon: Calculator },
  { id: "settings", label: "Impostazioni", Icon: Settings },
];

const WEEKDAY_NAMES = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];

const STATUS_LABELS = { confirmed: "Confermata", completed: "Completata", cancelled: "Annullata" };
const STATUS_STYLES = {
  confirmed: "bg-acqua text-petrolio",
  completed: "bg-salvia/40 text-antracite",
  cancelled: "bg-red-100 text-red-700",
};

const REVIEW_STATUS_LABELS = { pending: "Da approvare", approved: "Pubblicata", hidden: "Nascosta" };
const REVIEW_STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-acqua text-petrolio",
  hidden: "bg-antracite/10 text-antracite/60",
};

const EMPTY_TYPE = { name: "", description: "", duration_minutes: 30, price_label: "", active: true };

const gcalUrl = (b) => {
  const day = b.date.replace(/-/g, "");
  const [h, m] = b.time.split(":").map(Number);
  const total = h * 60 + m + b.duration_minutes;
  const end = `${String(Math.floor(total / 60)).padStart(2, "0")}${String(total % 60).padStart(2, "0")}`;
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: `${b.type_name} — ${b.first_name} ${b.last_name}`,
    dates: `${day}T${b.time.replace(":", "")}00/${day}T${end}00`,
    details: `Cliente: ${b.first_name} ${b.last_name}\nEmail: ${b.email}\nTelefono: ${b.phone || "-"}\nArgomento: ${b.topic || "-"}\nCodice prenotazione: ${b.id.slice(0, 8).toUpperCase()}`,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

export default function Admin() {
  const { secret } = useParams();
  const [token, setToken] = useState(() => sessionStorage.getItem("nx-admin-token") || "");
  const [authError, setAuthError] = useState(false);
  const [tab, setTab] = useState("bookings");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [replyDrafts, setReplyDrafts] = useState({});
  const [types, setTypes] = useState([]);
  const [availability, setAvailability] = useState({ rules: [], blocked: [] });
  const [settings, setSettings] = useState(null);
  const [newRule, setNewRule] = useState({ weekday: 0, start_time: "09:00", end_time: "12:00" });
  const [newBlocked, setNewBlocked] = useState({ date: "", time: "09:00", reason: "" });
  const [newType, setNewType] = useState(EMPTY_TYPE);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);

  const needsLogin = !secret && !token;
  const client = useMemo(() => adminApi(secret, token), [secret, token]);

  const guard = useCallback((p) => p.catch((e) => {
    if (e.response?.status === 401) {
      if (token) {
        sessionStorage.removeItem("nx-admin-token");
        setToken("");
        toast.error("Sessione scaduta. Accedi di nuovo.");
      } else {
        setAuthError(true);
      }
    } else {
      toast.error("Errore nel caricamento dei dati.");
    }
  }), [token]);

  const loadAll = useCallback(() => {
    guard(client.get("/admin/stats").then((r) => setStats(r.data)));
    guard(client.get("/admin/bookings").then((r) => setBookings(r.data)));
    guard(client.get("/admin/contacts").then((r) => setContacts(r.data)));
    guard(client.get("/admin/reviews").then((r) => setReviews(r.data)));
    guard(client.get("/admin/types").then((r) => setTypes(r.data)));
    guard(client.get("/admin/availability").then((r) => setAvailability(r.data)));
    guard(client.get("/admin/settings").then((r) => setSettings(r.data)));
  }, [client, guard]);

  useEffect(() => { if (!needsLogin) loadAll(); }, [loadAll, needsLogin]);

  const doLogin = async (e) => {
    e.preventDefault();
    setLoginBusy(true);
    setLoginError("");
    try {
      const r = await api.post("/auth/login", loginForm);
      sessionStorage.setItem("nx-admin-token", r.data.token);
      setToken(r.data.token);
      toast.success(`Benvenuto, ${r.data.name}.`);
    } catch (err) {
      const d = err.response?.data?.detail;
      setLoginError(typeof d === "string" ? d : "Accesso non riuscito. Riprova.");
    } finally {
      setLoginBusy(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("nx-admin-token");
    setToken("");
    window.location.href = "/admin";
  };

  const exportBookings = async () => {
    try {
      const r = await client.get("/admin/bookings/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `prenotazioni-alsera-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("File scaricato.");
    } catch {
      toast.error("Errore nell'esportazione.");
    }
  };

  const changePassword = async () => {
    if (pwForm.next.length < 8) {
      toast.error("La nuova password deve avere almeno 8 caratteri.");
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      toast.error("Le nuove password non coincidono.");
      return;
    }
    setPwBusy(true);
    try {
      await client.post("/auth/change-password", {
        current_password: pwForm.current,
        new_password: pwForm.next,
      });
      toast.success("Password aggiornata. Usala al prossimo accesso.");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Errore durante l'aggiornamento.");
    } finally {
      setPwBusy(false);
    }
  };

  const setBookingStatus = async (id, status) => {
    await guard(client.patch(`/admin/bookings/${id}`, { status }).then(() => {
      setBookings((b) => b.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(status === "completed"
        ? "Consulenza completata. Il cliente riceverà l'invito a lasciare una recensione."
        : "Prenotazione aggiornata.");
    }));
  };

  const setContactStatus = async (id, status) => {
    await guard(client.patch(`/admin/contacts/${id}`, { status }).then(() => {
      setContacts((c) => c.map((x) => (x.id === id ? { ...x, status } : x)));
    }));
  };

  const refreshStats = () => client.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});

  const setReviewStatus = async (id, status) => {
    await guard(client.patch(`/admin/reviews/${id}`, { status }).then(() => {
      setReviews((r) => r.map((x) => (x.id === id ? { ...x, status } : x)));
      refreshStats();
      toast.success("Recensione aggiornata.");
    }));
  };

  const deleteReview = async (id) => {
    await guard(client.delete(`/admin/reviews/${id}`).then(() => {
      setReviews((r) => r.filter((x) => x.id !== id));
      refreshStats();
      toast.success("Recensione eliminata.");
    }));
  };

  const deleteContact = async (id) => {
    await guard(client.delete(`/admin/contacts/${id}`).then(() => {
      setContacts((c) => c.filter((x) => x.id !== id));
      refreshStats();
      toast.success("Richiesta eliminata.");
    }));
  };

  const saveReply = async (id, value) => {
    const reply = (value ?? replyDrafts[id] ?? "").trim();
    await guard(client.patch(`/admin/reviews/${id}`, { reply }).then(() => {
      setReviews((rs) => rs.map((x) => (x.id === id ? { ...x, reply, reply_at: reply ? new Date().toISOString() : "" } : x)));
      setReplyDrafts((d) => ({ ...d, [id]: reply }));
      toast.success(reply ? "Risposta pubblicata sotto la recensione." : "Risposta rimossa.");
    }));
  };

  const addRule = async () => {
    await guard(client.post("/admin/availability/rules", { ...newRule, weekday: Number(newRule.weekday) }).then(() => {
      toast.success("Fascia oraria aggiunta.");
      client.get("/admin/availability").then((r) => setAvailability(r.data));
    }));
  };

  const deleteRule = async (id) => {
    await guard(client.delete(`/admin/availability/rules/${id}`).then(() => {
      setAvailability((a) => ({ ...a, rules: a.rules.filter((r) => r.id !== id) }));
    }));
  };

  const addBlocked = async () => {
    if (!newBlocked.date) { toast.error("Seleziona una data."); return; }
    await guard(client.post("/admin/availability/blocked", newBlocked).then(() => {
      toast.success("Orario bloccato.");
      client.get("/admin/availability").then((r) => setAvailability(r.data));
    }));
  };

  const deleteBlocked = async (id) => {
    await guard(client.delete(`/admin/availability/blocked/${id}`).then(() => {
      setAvailability((a) => ({ ...a, blocked: a.blocked.filter((b) => b.id !== id) }));
    }));
  };

  const saveType = async (t) => {
    await guard(client.put(`/admin/types/${t.id}`, t).then(() => toast.success("Tipologia aggiornata.")));
  };

  const createType = async () => {
    if (!newType.name) { toast.error("Inserisci un nome."); return; }
    await guard(client.post("/admin/types", { ...newType, duration_minutes: Number(newType.duration_minutes) }).then(() => {
      toast.success("Tipologia creata.");
      setNewType(EMPTY_TYPE);
      client.get("/admin/types").then((r) => setTypes(r.data));
    }));
  };

  const deleteType = async (id) => {
    await guard(client.delete(`/admin/types/${id}`).then(() => setTypes((t) => t.filter((x) => x.id !== id))));
  };

  const saveSettings = async () => {
    await guard(client.put("/admin/settings", settings).then(() => toast.success("Impostazioni salvate.")));
  };

  if (needsLogin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-avorio px-5" data-testid="admin-login-page">
        <div className="card-soft w-full max-w-md p-10">
          <div className="flex justify-center"><Logo /></div>
          <div className="mt-6 flex items-center justify-center gap-2 text-petrolio">
            <Lock className="h-4 w-4" />
            <h1 className="text-lg font-bold">Area riservata</h1>
          </div>
          <form onSubmit={doLogin} className="mt-6 space-y-4" data-testid="admin-login-form">
            <div>
              <label htmlFor="admin-email" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">Email</label>
              <input id="admin-email" data-testid="admin-login-email" type="email" required value={loginForm.email} onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))} className="input-field" autoComplete="username" />
            </div>
            <div>
              <label htmlFor="admin-password" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">Password</label>
              <input id="admin-password" data-testid="admin-login-password" type="password" required value={loginForm.password} onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))} className="input-field" autoComplete="current-password" />
            </div>
            {loginError && <p className="rounded-xl bg-red-50 p-3 text-sm font-semibold text-red-700" data-testid="admin-login-error">{loginError}</p>}
            <button type="submit" disabled={loginBusy} className="btn-primary w-full disabled:opacity-60" data-testid="admin-login-submit">
              {loginBusy ? "Accesso in corso…" : "Accedi"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-avorio px-5" data-testid="admin-unauthorized">
        <div className="card-soft max-w-md p-10 text-center">
          <ShieldAlert className="mx-auto h-10 w-10 text-petrolio" />
          <h1 className="mt-4 text-2xl font-bold text-antracite">Accesso non autorizzato</h1>
          <p className="mt-2 text-sm text-antracite/65">
            Questo pannello è riservato all'amministratore.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-avorio" data-testid="admin-dashboard">
      <header className="border-b border-salvia/30 bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-5 py-4 sm:px-8">
          <Logo />
          <div className="flex items-center gap-3">
            <span className="rounded-full bg-petrolio px-4 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-avorio">
              Pannello di gestione
            </span>
            {token && (
              <button type="button" onClick={logout} className="inline-flex items-center gap-1.5 rounded-full border border-salvia/50 px-4 py-1.5 text-xs font-bold text-antracite/70 transition-colors hover:bg-acqua" data-testid="admin-logout">
                <LogOut className="h-3.5 w-3.5" /> Esci
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-5 py-8 sm:px-8">
        {stats && (
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="admin-stats">
            {[
              { label: "Prenotazioni totali", value: stats.total_bookings },
              { label: "Prenotazioni attive", value: stats.active_bookings },
              { label: "Questo mese", value: stats.month_bookings },
              { label: "Richieste da leggere", value: stats.new_contacts },
            ].map((s) => (
              <div key={s.label} className="card-soft p-5">
                <p className="text-3xl font-extrabold text-petrolio">{s.value}</p>
                <p className="mt-1 text-xs font-semibold uppercase tracking-[0.12em] text-antracite/55">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-tabs">
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${
                tab === id ? "bg-petrolio text-avorio" : "bg-white text-antracite/65 hover:bg-acqua"
              }`}
            >
              <Icon className="h-4 w-4" /> {label}
              {id === "reviews" && stats?.pending_reviews > 0 && (
                <span className="rounded-full bg-amber-400 px-2 py-0.5 text-[0.65rem] font-extrabold text-antracite" data-testid="admin-reviews-pending-badge">
                  {stats.pending_reviews}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "bookings" && (
            <div className="space-y-4" data-testid="admin-bookings">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm font-semibold text-antracite/60">
                  {bookings.length} {bookings.length === 1 ? "prenotazione" : "prenotazioni"} in archivio
                </p>
                <button type="button" onClick={exportBookings} className="btn-secondary !px-5 !py-2.5" data-testid="admin-export-bookings">
                  <Download className="h-4 w-4" /> Esporta elenco (CSV)
                </button>
              </div>
              {bookings.length === 0 && <p className="card-soft p-8 text-sm text-antracite/60">Nessuna prenotazione al momento.</p>}
              {bookings.map((b) => (
                <div key={b.id} className="card-soft flex flex-wrap items-center gap-4 p-5" data-testid={`admin-booking-${b.id.slice(0, 8)}`}>
                  <div className="min-w-[140px]">
                    <p className="font-bold capitalize text-antracite">{format(parseISO(b.date), "EEE d MMM yyyy", { locale: it })}</p>
                    <p className="text-sm font-semibold text-petrolio">{b.time} · {b.duration_minutes} min</p>
                  </div>
                  <div className="min-w-[180px] flex-1">
                    <p className="font-bold text-antracite">{b.first_name} {b.last_name}</p>
                    <p className="text-sm text-antracite/60">{b.type_name}</p>
                    <p className="text-sm text-antracite/60">{b.email}{b.phone ? ` · ${b.phone}` : ""}</p>
                    {b.topic && <p className="mt-1 text-sm italic text-antracite/55">"{b.topic}"</p>}
                  </div>
                  <span className={`rounded-full px-3 py-1 text-xs font-bold ${STATUS_STYLES[b.status]}`} data-testid={`admin-booking-status-${b.id.slice(0, 8)}`}>
                    {STATUS_LABELS[b.status]}
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {b.status === "confirmed" && (
                      <a
                        href={gcalUrl(b)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-full bg-petrolio px-4 py-2 text-xs font-bold text-avorio transition-colors hover:bg-petrolioDeep"
                        data-testid={`admin-booking-gcal-${b.id.slice(0, 8)}`}
                      >
                        <CalendarPlus className="h-3.5 w-3.5" /> Aggiungi a Google Calendar
                      </a>
                    )}
                    {b.status === "confirmed" && (
                      <>
                        <button type="button" onClick={() => setBookingStatus(b.id, "completed")} className="rounded-full bg-acqua px-4 py-2 text-xs font-bold text-petrolio transition-colors hover:bg-salvia/60" data-testid={`admin-booking-complete-${b.id.slice(0, 8)}`}>
                          Completa
                        </button>
                        <button type="button" onClick={() => setBookingStatus(b.id, "cancelled")} className="rounded-full bg-red-50 px-4 py-2 text-xs font-bold text-red-700 transition-colors hover:bg-red-100" data-testid={`admin-booking-cancel-${b.id.slice(0, 8)}`}>
                          Annulla
                        </button>
                      </>
                    )}
                    {b.status !== "confirmed" && (
                      <button type="button" onClick={() => setBookingStatus(b.id, "confirmed")} className="rounded-full bg-acqua px-4 py-2 text-xs font-bold text-petrolio transition-colors hover:bg-salvia/60" data-testid={`admin-booking-restore-${b.id.slice(0, 8)}`}>
                        Ripristina
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "availability" && (
            <div className="grid gap-6 lg:grid-cols-2" data-testid="admin-availability">
              <div className="card-soft p-6">
                <h2 className="text-lg font-bold text-antracite">Fasce orarie settimanali</h2>
                <p className="mt-1 text-sm text-antracite/60">Gli slot di prenotazione vengono generati da queste fasce.</p>
                <div className="mt-5 space-y-3">
                  {availability.rules.map((r) => (
                    <div key={r.id} className="flex items-center justify-between rounded-2xl bg-acqua/50 px-4 py-3" data-testid={`admin-rule-${r.id.slice(0, 8)}`}>
                      <p className="text-sm font-semibold text-antracite">
                        {WEEKDAY_NAMES[r.weekday]} · {r.start_time} – {r.end_time}
                      </p>
                      <button type="button" onClick={() => deleteRule(r.id)} className="rounded-full p-2 text-red-600 transition-colors hover:bg-red-50" aria-label="Elimina fascia" data-testid={`admin-rule-delete-${r.id.slice(0, 8)}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  {availability.rules.length === 0 && <p className="text-sm text-antracite/50">Nessuna fascia configurata.</p>}
                </div>
                <div className="mt-6 flex flex-wrap items-end gap-3 border-t border-salvia/30 pt-5">
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-antracite/55" htmlFor="rule-day">Giorno</label>
                    <select id="rule-day" data-testid="admin-rule-weekday" value={newRule.weekday} onChange={(e) => setNewRule((r) => ({ ...r, weekday: e.target.value }))} className="input-field !w-auto">
                      {WEEKDAY_NAMES.map((n, i) => <option key={n} value={i}>{n}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-antracite/55" htmlFor="rule-start">Dalle</label>
                    <input id="rule-start" data-testid="admin-rule-start" type="time" value={newRule.start_time} onChange={(e) => setNewRule((r) => ({ ...r, start_time: e.target.value }))} className="input-field !w-auto" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-antracite/55" htmlFor="rule-end">Alle</label>
                    <input id="rule-end" data-testid="admin-rule-end" type="time" value={newRule.end_time} onChange={(e) => setNewRule((r) => ({ ...r, end_time: e.target.value }))} className="input-field !w-auto" />
                  </div>
                  <button type="button" onClick={addRule} className="btn-primary !px-5 !py-2.5" data-testid="admin-rule-add">
                    <Plus className="h-4 w-4" /> Aggiungi
                  </button>
                </div>
              </div>

              <div className="card-soft p-6">
                <h2 className="text-lg font-bold text-antracite">Orari bloccati</h2>
                <p className="mt-1 text-sm text-antracite/60">Blocca singoli orari in date specifiche (impegni, ferie, festività).</p>
                <div className="mt-5 space-y-3">
                  {availability.blocked.map((b) => (
                    <div key={b.id} className="flex items-center justify-between rounded-2xl bg-acqua/50 px-4 py-3" data-testid={`admin-blocked-${b.id.slice(0, 8)}`}>
                      <p className="text-sm font-semibold text-antracite">
                        {format(parseISO(b.date), "d MMM yyyy", { locale: it })} · {b.time}{b.reason ? ` — ${b.reason}` : ""}
                      </p>
                      <button type="button" onClick={() => deleteBlocked(b.id)} className="rounded-full p-2 text-red-600 transition-colors hover:bg-red-50" aria-label="Elimina blocco" data-testid={`admin-blocked-delete-${b.id.slice(0, 8)}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  {availability.blocked.length === 0 && <p className="text-sm text-antracite/50">Nessun orario bloccato.</p>}
                </div>
                <div className="mt-6 flex flex-wrap items-end gap-3 border-t border-salvia/30 pt-5">
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-antracite/55" htmlFor="blocked-date">Data</label>
                    <input id="blocked-date" data-testid="admin-blocked-date" type="date" value={newBlocked.date} onChange={(e) => setNewBlocked((b) => ({ ...b, date: e.target.value }))} className="input-field !w-auto" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-antracite/55" htmlFor="blocked-time">Ora</label>
                    <input id="blocked-time" data-testid="admin-blocked-time" type="time" step="1800" value={newBlocked.time} onChange={(e) => setNewBlocked((b) => ({ ...b, time: e.target.value }))} className="input-field !w-auto" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-antracite/55" htmlFor="blocked-reason">Motivo</label>
                    <input id="blocked-reason" data-testid="admin-blocked-reason" value={newBlocked.reason} onChange={(e) => setNewBlocked((b) => ({ ...b, reason: e.target.value }))} className="input-field !w-auto" placeholder="Facoltativo" />
                  </div>
                  <button type="button" onClick={addBlocked} className="btn-primary !px-5 !py-2.5" data-testid="admin-blocked-add">
                    <Plus className="h-4 w-4" /> Blocca
                  </button>
                </div>
              </div>
            </div>
          )}

          {tab === "types" && (
            <div className="space-y-4" data-testid="admin-types">
              {types.map((t) => (
                <div key={t.id} className="card-soft p-6" data-testid={`admin-type-${t.id.slice(0, 8)}`}>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-antracite/55">Nome</label>
                      <input value={t.name} onChange={(e) => setTypes((ts) => ts.map((x) => x.id === t.id ? { ...x, name: e.target.value } : x))} className="input-field" data-testid={`admin-type-name-${t.id.slice(0, 8)}`} />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-antracite/55">Durata (min)</label>
                      <input type="number" min="15" step="15" value={t.duration_minutes} onChange={(e) => setTypes((ts) => ts.map((x) => x.id === t.id ? { ...x, duration_minutes: Number(e.target.value) } : x))} className="input-field" data-testid={`admin-type-duration-${t.id.slice(0, 8)}`} />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-antracite/55">Etichetta prezzo</label>
                      <input value={t.price_label} onChange={(e) => setTypes((ts) => ts.map((x) => x.id === t.id ? { ...x, price_label: e.target.value } : x))} className="input-field" placeholder="Es. Gratuita" data-testid={`admin-type-price-${t.id.slice(0, 8)}`} />
                    </div>
                    <div className="flex items-end gap-3 pb-1">
                      <label className="flex items-center gap-2 text-sm font-semibold text-antracite/70">
                        <input type="checkbox" checked={t.active} onChange={(e) => setTypes((ts) => ts.map((x) => x.id === t.id ? { ...x, active: e.target.checked } : x))} className="h-4 w-4 accent-petrolio" data-testid={`admin-type-active-${t.id.slice(0, 8)}`} />
                        Attiva
                      </label>
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-antracite/55">Descrizione</label>
                    <input value={t.description} onChange={(e) => setTypes((ts) => ts.map((x) => x.id === t.id ? { ...x, description: e.target.value } : x))} className="input-field" />
                  </div>
                  <div className="mt-4 flex gap-3">
                    <button type="button" onClick={() => saveType(t)} className="btn-primary !px-5 !py-2.5" data-testid={`admin-type-save-${t.id.slice(0, 8)}`}>Salva</button>
                    <button type="button" onClick={() => deleteType(t.id)} className="rounded-full bg-red-50 px-5 py-2.5 text-sm font-bold text-red-700 transition-colors hover:bg-red-100" data-testid={`admin-type-delete-${t.id.slice(0, 8)}`}>Elimina</button>
                  </div>
                </div>
              ))}

              <div className="card-soft border-dashed p-6" data-testid="admin-type-new">
                <h2 className="text-lg font-bold text-antracite">Nuova tipologia</h2>
                <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <input value={newType.name} onChange={(e) => setNewType((t) => ({ ...t, name: e.target.value }))} className="input-field" placeholder="Nome" data-testid="admin-new-type-name" />
                  <input type="number" min="15" step="15" value={newType.duration_minutes} onChange={(e) => setNewType((t) => ({ ...t, duration_minutes: e.target.value }))} className="input-field" placeholder="Durata (min)" data-testid="admin-new-type-duration" />
                  <input value={newType.price_label} onChange={(e) => setNewType((t) => ({ ...t, price_label: e.target.value }))} className="input-field" placeholder="Etichetta prezzo" data-testid="admin-new-type-price" />
                  <button type="button" onClick={createType} className="btn-primary !px-5 !py-2.5" data-testid="admin-new-type-create">
                    <Plus className="h-4 w-4" /> Crea
                  </button>
                </div>
                <input value={newType.description} onChange={(e) => setNewType((t) => ({ ...t, description: e.target.value }))} className="input-field mt-4" placeholder="Descrizione" data-testid="admin-new-type-description" />
              </div>
            </div>
          )}

          {tab === "contacts" && (
            <div className="space-y-4" data-testid="admin-contacts">
              {contacts.length === 0 && <p className="card-soft p-8 text-sm text-antracite/60">Nessuna richiesta di contatto al momento.</p>}
              {contacts.map((c) => (
                <div key={c.id} className="card-soft p-6" data-testid={`admin-contact-${c.id.slice(0, 8)}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-bold text-antracite">{c.name} {c.company ? <span className="font-medium text-antracite/55">· {c.company}</span> : null}</p>
                      <p className="text-sm text-antracite/60">{c.email}</p>
                      <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-petrolio">
                        {c.service_type}{c.budget ? ` · Budget: ${c.budget}` : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`rounded-full px-3 py-1 text-xs font-bold ${c.status === "new" ? "bg-petrolio text-avorio" : "bg-acqua text-petrolio"}`}>
                        {c.status === "new" ? "Nuova" : "Letta"}
                      </span>
                      <button type="button" onClick={() => setContactStatus(c.id, c.status === "new" ? "read" : "new")} className="rounded-full bg-acqua px-4 py-2 text-xs font-bold text-petrolio transition-colors hover:bg-salvia/60" data-testid={`admin-contact-toggle-${c.id.slice(0, 8)}`}>
                        Segna come {c.status === "new" ? "letta" : "nuova"}
                      </button>
                      <button type="button" onClick={() => deleteContact(c.id)} className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-4 py-2 text-xs font-bold text-red-700 transition-colors hover:bg-red-100" data-testid={`admin-contact-delete-${c.id.slice(0, 8)}`}>
                        <Trash2 className="h-3.5 w-3.5" /> Elimina
                      </button>
                    </div>
                  </div>
                  <p className="mt-3 whitespace-pre-wrap rounded-2xl bg-acqua/40 p-4 text-sm leading-relaxed text-antracite/75">{c.message}</p>
                </div>
              ))}
            </div>
          )}

          {tab === "reviews" && (
            <div className="space-y-4" data-testid="admin-reviews">
              <p className="text-sm font-semibold text-antracite/60">
                {reviews.length} {reviews.length === 1 ? "recensione ricevuta" : "recensioni ricevute"} — visibili sul sito solo dopo l'approvazione.
              </p>
              {reviews.length === 0 && <p className="card-soft p-8 text-sm text-antracite/60">Nessuna recensione ricevuta al momento.</p>}
              {reviews.map((r) => (
                <div key={r.id} className="card-soft p-6" data-testid={`admin-review-${r.id.slice(0, 8)}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex gap-0.5" aria-label={`${r.rating}/5`}>
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star key={n} className={`h-4 w-4 ${n <= r.rating ? "fill-amber-400 text-amber-400" : "text-salvia/50"}`} />
                        ))}
                      </div>
                      <p className="mt-2 font-bold text-antracite">{r.name}</p>
                      <p className="text-xs text-antracite/50">
                        {r.created_at ? format(parseISO(r.created_at), "d MMM yyyy, HH:mm", { locale: it }) : ""}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-3 py-1 text-xs font-bold ${REVIEW_STATUS_STYLES[r.status]}`} data-testid={`admin-review-status-${r.id.slice(0, 8)}`}>
                        {REVIEW_STATUS_LABELS[r.status]}
                      </span>
                      {r.status !== "approved" && (
                        <button type="button" onClick={() => setReviewStatus(r.id, "approved")} className="rounded-full bg-petrolio px-4 py-2 text-xs font-bold text-avorio transition-colors hover:bg-petrolioDeep" data-testid={`admin-review-approve-${r.id.slice(0, 8)}`}>
                          Approva
                        </button>
                      )}
                      {r.status === "approved" && (
                        <button type="button" onClick={() => setReviewStatus(r.id, "hidden")} className="rounded-full bg-acqua px-4 py-2 text-xs font-bold text-petrolio transition-colors hover:bg-salvia/60" data-testid={`admin-review-hide-${r.id.slice(0, 8)}`}>
                          Nascondi
                        </button>
                      )}
                      <button type="button" onClick={() => deleteReview(r.id)} className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-4 py-2 text-xs font-bold text-red-700 transition-colors hover:bg-red-100" data-testid={`admin-review-delete-${r.id.slice(0, 8)}`}>
                        <Trash2 className="h-3.5 w-3.5" /> Elimina
                      </button>
                    </div>
                  </div>
                  <p className="mt-3 whitespace-pre-wrap rounded-2xl bg-acqua/40 p-4 text-sm leading-relaxed text-antracite/75">{r.text}</p>
                  <div className="mt-3 rounded-2xl border border-salvia/30 p-4">
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60" htmlFor={`reply-${r.id.slice(0, 8)}`}>
                      La tua risposta pubblica
                    </label>
                    <textarea
                      id={`reply-${r.id.slice(0, 8)}`}
                      rows={2}
                      maxLength={500}
                      className="input-field resize-none"
                      placeholder="Rispondi alla recensione: sarà visibile sul sito sotto la testimonianza…"
                      value={replyDrafts[r.id] ?? r.reply ?? ""}
                      onChange={(e) => setReplyDrafts((d) => ({ ...d, [r.id]: e.target.value }))}
                      data-testid={`admin-review-reply-${r.id.slice(0, 8)}`}
                    />
                    <div className="mt-2 flex flex-wrap gap-2">
                      <button type="button" onClick={() => saveReply(r.id)} className="rounded-full bg-petrolio px-4 py-2 text-xs font-bold text-avorio transition-colors hover:bg-petrolioDeep" data-testid={`admin-review-reply-save-${r.id.slice(0, 8)}`}>
                        {r.reply ? "Salva risposta" : "Pubblica risposta"}
                      </button>
                      {r.reply && (
                        <button type="button" onClick={() => saveReply(r.id, "")} className="rounded-full bg-red-50 px-4 py-2 text-xs font-bold text-red-700 transition-colors hover:bg-red-100" data-testid={`admin-review-reply-remove-${r.id.slice(0, 8)}`}>
                          Rimuovi risposta
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "blog" && <AdminBlog client={client} guard={guard} />}
          {tab === "quotes" && <AdminQuotes client={client} guard={guard} />}

          {tab === "settings" && settings && (
            <div className="grid max-w-5xl gap-6 lg:grid-cols-2">
              <div className="card-soft p-6 sm:p-8" data-testid="admin-settings">
                <h2 className="text-lg font-bold text-antracite">Recapiti e informazioni pubbliche</h2>
                <p className="mt-1 text-sm text-antracite/60">Questi dati vengono mostrati nella pagina Contatti e nel footer del sito.</p>
                <div className="mt-6 space-y-4">
                  {[
                    { key: "contact_email", label: "Email aziendale", testid: "admin-settings-email" },
                    { key: "phone", label: "Telefono", testid: "admin-settings-phone" },
                    { key: "response_time", label: "Tempi di risposta (es. entro 2 giorni lavorativi)", testid: "admin-settings-response" },
                    { key: "linkedin", label: "LinkedIn (URL)", testid: "admin-settings-linkedin" },
                    { key: "instagram", label: "Instagram (URL)", testid: "admin-settings-instagram" },
                    { key: "facebook", label: "Facebook (URL)", testid: "admin-settings-facebook" },
                    { key: "legal_info", label: "Dati legali (ragione sociale, P.IVA, sede)", testid: "admin-settings-legal" },
                  ].map((f) => (
                    <div key={f.key}>
                      <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">{f.label}</label>
                      <input value={settings[f.key] || ""} onChange={(e) => setSettings((s) => ({ ...s, [f.key]: e.target.value }))} className="input-field" data-testid={f.testid} />
                    </div>
                  ))}
                  <button type="button" onClick={saveSettings} className="btn-primary" data-testid="admin-settings-save">Salva impostazioni</button>
                </div>
              </div>

              <div className="card-soft h-fit p-6 sm:p-8" data-testid="admin-security">
                <div className="flex items-center gap-3">
                  <KeyRound className="h-5 w-5 text-petrolio" />
                  <h2 className="text-lg font-bold text-antracite">Sicurezza</h2>
                </div>
                <p className="mt-1 text-sm text-antracite/60">Cambia la password di accesso all'area riservata.</p>
                <div className="mt-6 space-y-4">
                  <div>
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60" htmlFor="pw-current">Password attuale</label>
                    <input id="pw-current" type="password" value={pwForm.current} onChange={(e) => setPwForm((f) => ({ ...f, current: e.target.value }))} className="input-field" autoComplete="current-password" data-testid="admin-pw-current" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60" htmlFor="pw-next">Nuova password</label>
                    <input id="pw-next" type="password" value={pwForm.next} onChange={(e) => setPwForm((f) => ({ ...f, next: e.target.value }))} className="input-field" autoComplete="new-password" data-testid="admin-pw-next" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60" htmlFor="pw-confirm">Conferma nuova password</label>
                    <input id="pw-confirm" type="password" value={pwForm.confirm} onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))} className="input-field" autoComplete="new-password" data-testid="admin-pw-confirm" />
                  </div>
                  <button type="button" onClick={changePassword} disabled={pwBusy || !pwForm.current || !pwForm.next} className="btn-primary disabled:opacity-40" data-testid="admin-pw-save">
                    {pwBusy ? "Aggiornamento…" : "Cambia password"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
